import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageSquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { FLAG_COMPANIES_WEBCHAT } from "@bo/shared";
import { api } from "@/lib/api.js";
import { useAuthStore } from "@/lib/auth.js";
import { claves, webchatsApi } from "@/lib/webchat/api.js";
import { interpretarError } from "@/lib/webchat/errores.js";
import { ESTADO_WEBCHAT } from "@/lib/webchat/estado.js";
import { AvisoError } from "./Avisos.js";
import { Modal } from "./Dialogos.js";
import { StatusDot } from "./StatusDot.js";

interface Flag {
  id: number;
  flagKey: string;
  enabledGlobal: boolean;
  overrides: { idcompany: number; enabled: boolean; note: string | null }[];
}

function DialogoHabilitar({
  habilitar,
  ocupado,
  error,
  onCerrar,
  onConfirmar,
}: Readonly<{
  habilitar: boolean;
  ocupado: boolean;
  error: unknown;
  onCerrar: () => void;
  onConfirmar: (nota: string) => void;
}>) {
  const [nota, setNota] = useState("");
  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      titulo={habilitar ? "Incluir en el módulo Webchats" : "Excluir del módulo Webchats"}
      descripcion={
        habilitar
          ? "El equipo va a poder crear y editar los webchats de esta company."
          : "Sus webchats dejan de verse y editarse en este módulo. No se toca ningún webchat."
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onConfirmar(nota.trim());
        }}
      >
        <label className="block space-y-1">
          <span className="text-sm font-medium">Nota (opcional)</span>
          <input className="input" maxLength={255} value={nota} onChange={(e) => setNota(e.target.value)} />
        </label>
        {error ? <AvisoError error={interpretarError(error)} /> : null}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={ocupado}>
            Confirmar
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * La sección «Webchats» de la ficha de una company: sus webchats y el acceso al
 * alta. El módulo administra todas (global del flag webchat_social_isoft); quien
 * gestiona los flags puede excluir una, con un override apagado.
 */
export function WebchatsDeCompany({ idcompany }: Readonly<{ idcompany: number }>) {
  const qc = useQueryClient();
  const puedeLeer = useAuthStore((s) => s.hasPermission("webchat.read"));
  const puedeCrear = useAuthStore((s) => s.hasPermission("webchat.write"));
  const administraFlags = useAuthStore((s) => s.hasPermission("feature_flag.write"));
  const [dialogo, setDialogo] = useState(false);
  const flags = useQuery({ queryKey: ["feature-flags"], queryFn: () => api.get<{ items: Flag[] }>("/feature-flags"), enabled: puedeLeer || administraFlags });
  const flag = flags.data?.items.find((f) => f.flagKey === FLAG_COMPANIES_WEBCHAT);
  const habilitada = flag?.overrides.find((o) => o.idcompany === idcompany)?.enabled ?? flag?.enabledGlobal ?? false;
  // Con el global encendido, una company inactiva figura habilitada pero no se le puede crear nada.
  const catalogos = useQuery({ queryKey: claves.catalogos(null), queryFn: () => webchatsApi.catalogos(null), staleTime: 300_000, enabled: puedeCrear && habilitada });
  const activa = catalogos.data?.companiesHabilitadas.some((c) => c.idcompany === idcompany && c.activa) ?? false;
  const webchats = useQuery({
    queryKey: claves.listado({ idcompany, page: 1, pageSize: 50 }),
    queryFn: () => webchatsApi.listar({ idcompany, page: 1, pageSize: 50 }),
    enabled: puedeLeer && habilitada,
  });
  const cambiar = useMutation({
    mutationFn: (nota: string) =>
      api.put(`/feature-flags/${flag?.id ?? 0}/override`, { idcompany, enabled: !habilitada, ...(nota ? { note: nota } : {}) }),
    onSuccess: () => {
      toast.success(habilitada ? "La company quedó excluida del módulo Webchats" : "Company habilitada para Webchats");
      setDialogo(false);
      void qc.invalidateQueries({ queryKey: ["feature-flags"] });
      void qc.invalidateQueries({ queryKey: claves.raiz });
    },
  });

  if (!puedeLeer && !administraFlags) return null;
  return (
    <section className="card relative" aria-labelledby="titulo-webchats-company">
      <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="titulo-webchats-company" className="font-semibold inline-flex items-center gap-2">
            <MessageSquare size={16} aria-hidden="true" /> Webchats
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {flags.data && (
              <StatusDot
                tono={habilitada ? "ok" : "neutro"}
                etiqueta={habilitada ? "Administrada desde este módulo" : "Excluida del módulo Webchats"}
                className="text-xs"
              />
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {administraFlags && flag && (
            <button type="button" className="btn-secondary text-sm" onClick={() => setDialogo(true)}>
              {habilitada ? "Excluir del módulo" : "Incluir en el módulo"}
            </button>
          )}
          {puedeCrear && habilitada && activa && (
            <Link to={`/webchats/nuevo?idcompany=${idcompany}`} className="btn-primary text-sm inline-flex items-center gap-1">
              <Plus size={14} aria-hidden="true" /> Nuevo webchat
            </Link>
          )}
        </div>
      </header>
      <div className="p-5">
        {flags.isLoading && <Loader2 size={16} className="animate-spin text-slate-400" aria-label="Cargando" />}
        {flags.error ? <AvisoError error={interpretarError(flags.error)} /> : null}
        {catalogos.error ? <AvisoError error={interpretarError(catalogos.error)} /> : null}
        {puedeCrear && habilitada && catalogos.data && !activa && <p className="text-sm text-slate-500">Esta company está inactiva: no se le pueden crear webchats.</p>}
        {!habilitada && flags.data && (
          <p className="text-sm text-slate-500">Esta company está excluida del módulo Webchats.</p>
        )}
        {habilitada && puedeLeer && webchats.data && webchats.data.items.length === 0 && (
          <p className="text-sm text-slate-500">Esta company todavía no tiene webchats.</p>
        )}
        {habilitada && puedeLeer && (webchats.data?.items.length ?? 0) > 0 && (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {webchats.data?.items.map((w) => {
              const e = ESTADO_WEBCHAT[w.estado];
              const destino = w.idcanal ? `/webchats/${w.idcanal}` : `/webchats/operaciones/${w.operacionId ?? 0}`;
              return (
                <li key={`${w.idcanal ?? "op"}-${w.operacionId ?? w.host}`} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                  <Link to={destino} className="mono text-[13px] font-medium hover:underline">
                    {w.host}
                  </Link>
                  <span className="text-slate-500">{w.nombre ?? ""}</span>
                  <StatusDot tono={e.tono} etiqueta={e.etiqueta} className="ml-auto" />
                </li>
              );
            })}
          </ul>
        )}
        {webchats.error && <AvisoError error={interpretarError(webchats.error)} />}
      </div>
      {dialogo && (
        <DialogoHabilitar
          habilitar={!habilitada}
          ocupado={cambiar.isPending}
          error={cambiar.error}
          onCerrar={() => setDialogo(false)}
          onConfirmar={(nota) => cambiar.mutate(nota)}
        />
      )}
    </section>
  );
}
